import { COST } from "components/Budgeting2023/options/margin/FormulasTab"
import { isNil, isNaN } from "lodash"
import { partidaGetClientPrice } from "./functions"

export const getMargin = partida => {
  if (partida.hasOwnProperty("margin") && !isNil(partida.margin)) {
    return parseFloat(partida.margin)
  } else {
    return (
      parseFloat(partida.costeGeneral) +
      parseFloat(partida.costeIndirecto) +
      parseFloat(partida.industrialProfit)
    )
  }
}

export const calcMarginFactor = partida => {
  return 1 - getMargin(partida) / 100
}

export const marginCalc = (partida, formula) => {
  //console.log(`marginCalc: price => ${partida.price}, clientPrice => ${partida.clientPrice}`)
  if (
    !isNaN(partida.price) &&
    !isNaN(partida.clientPrice) &&
    partida.clientPrice !== 0
  ) {
    return calcMargin(partida.price, partida.clientPrice, formula)
  } else {
    return partida.margin || 0
  }
}

export const calcCostTotal = partida => {
  var costTotal = parseFloat(partida.price) * parseFloat(partida.quantity)
  //console.log(`calcCostTotal: costTotal => ${costTotal}`)
  return costTotal
}

export const calcCostRealTotal = partida => {
  var costRealTotal =
    parseFloat(!isNil(partida.costReal) ? partida.costReal : partida.price) *
    parseFloat(partida.quantity)
  //console.log(`calcCostRealTotal: costRealTotal => ${costRealTotal}`)
  return costRealTotal
}

export const calcApplyDiscount = (price, discount) => {
  return price * (1 - discount / 100)
}

export const calcClientPriceWithoutDiscount = (partida, formula) => {
  if (isNaN(partida.price) || partida.price === 0) {
    return 0
  } else {
    let clientPriceWithoutDiscount = 0
    if (formula === COST) {
      const margin = getMargin(partida)
      //part.price * (1 + margins/100) (editado)
      clientPriceWithoutDiscount = partida.price * (1 + margin / 100)
    } else {
      const margin = calcMarginFactor(partida, formula)
      clientPriceWithoutDiscount = margin
        ? parseFloat(partida.price) / calcMarginFactor(partida)
        : partida.price
    }

    return clientPriceWithoutDiscount
  }
}

export const calcClientPrice = (partida, formula) => {
  if (isNil(partida.discount) || partida.discount === 0) {
    return calcClientPriceWithoutDiscount(partida, formula)
  } else {
    return (
      calcClientPriceWithoutDiscount(partida, formula) *
      (1 - parseFloat(partida.discount) / 100)
    )
  }
}

export const calcPriceTotal = (partida, formula) => {
  var priceTotal = partida.clientPrice * partida.quantity
  //console.log(`calcPriceTotal: priceTotal => ${priceTotal}`)
  return priceTotal
}

export const costRealCalc = partida => {
  //console.log(`partida: ${JSON.stringify(partida)}`)
  return !isNil(partida.costReal)
    ? parseFloat(partida.costReal)
    : parseFloat(partida.price)
}

export const costRealTotalCalc = partida => {
  //console.log('costRealTotalCalc:', costRealCalc(partida), partida.quantity)
  return costRealCalc(partida) * parseFloat(partida.quantity)
}

export const marginRealCalc = partida => {
  return (
    calcPriceTotal(partida) - calcCostRealTotal(partida) ||
    calcPriceTotal(partida) - calcCostTotal(partida)
  )
}

export const underObjective = partida => {
  return partida.costReal && partida.costReal < partida.price
}

export const overObjective = partida => {
  return partida.costReal && partida.costReal > partida.price
}

export const overPrice = partida => {
  return partida.costReal && partida.costReal > partida.clientPrice
}

export const calcPartida = (partida, updatedField, updatedValue, formula) => {
  partida[updatedField] = updatedValue

  switch (updatedField) {
    case "quantity":
    case "price":
    case "costReal":
    case "discount":
    case "margin":
      partida.clientPriceWithoutDiscount = calcClientPriceWithoutDiscount(
        partida,
        formula,
      )
      partida.clientPrice = calcClientPrice(partida, formula)
    case "clientPrice":
    case "clientPriceWithoutDiscount":
      partida.margin = marginCalc(partida, formula)
    //partida.marginReal = marginRealCalc(partida)
    //partida.marginRealPerc = marginRealPercCalc(partida)
    default:
      break
  }

  return partida
}

export const calcularMargenFormulaClient = (price, cost) => {
  if (
    typeof cost !== "number" ||
    typeof price !== "number" ||
    cost <= 0 ||
    price <= 0
  ) {
    throw new Error("Cost and price must be positive numbers.")
  }
  return ((price - cost) / price) * 100 // Margin over price (client)
}

export const calcularMargenRealPorcentaje = (partida, formula) => {
  const { price, priceReduced, costReal, clientPrice } = partida
  const priceValue = costReal ?? priceReduced ?? price
  if (!clientPrice) return 0

  const margin =
    formula !== COST
      ? 100 * (1 - priceValue / clientPrice)
      : ((clientPrice - priceValue) / priceValue) * 100

  return margin
}

export const margenPercentage = (partida, formula) => {
  const { price, clientPrice } = partida
  if (!clientPrice) return 0
  if (!price) return 0

  let margin = 0
  //formula === COST? clientPrice - price : price - clientPric
  if (formula === COST) {
    const x1 = clientPrice - price
    const x2 = x1 / price
    const x3 = x2 * 100
    margin = x3
  } else {
    const x1 = price / clientPrice
    const x2 = 1 - x1
    const x3 = 100 * x2
    margin = x3
  }

  return margin
}

// *********************

export const calcMarginWithCostFormula = (cost, price) => {
  const margin = price / cost - 1
  return margin * 100
}

export const calcMarginWithClientFormula = (cost, price) => {
  const margin = 1 - cost / price
  return margin * 100
}

export const calcMargin = (cost, price, formula) =>
  formula === COST
    ? calcMarginWithCostFormula(cost, price)
    : calcMarginWithClientFormula(cost, price)

export const applyCostMargin = (price, marginPercent) => {
  return price * (1 + marginPercent / 100)
}

export const applyClientMargin = (price, margin) => {
  const newMargin = margin / 100
  const divisor = 1 - newMargin
  const finalPrice = price / divisor

  return isFinite(finalPrice) ? finalPrice : 0
}

export function calculateTotalWithDiscount(price, quantity, discount) {
  if (!price || !quantity) return 0
  if (!discount || discount <= 0) return price * quantity
  const discountedPrice = price - (discount / 100) * price

  return discountedPrice * quantity
}

/**
 * Calculates the reduced price after applying a reduction percentage.
 * If the reduction is 0, null or undefined, it returns the original price as-is.
 * @param {number} price - The original price before reduction.
 * @param {number} costReduction - The reduction percentage (e.g., 10 for 10%).
 * @returns {number} - The reduced price.
 */
export const applyCostReduction = (price, costReduction) => {
  if (!costReduction || isNaN(costReduction)) {
    return price
  }

  const costReductionDecimal = costReduction / 100
  const priceReduced = price * (1 - costReductionDecimal)
  return priceReduced
}
